import React, {useCallback, useEffect, useState, useRef, useLayoutEffect} from 'react';
import {gsap} from 'gsap';
import {Flip} from "gsap/Flip";
import { handleAutoScroll, usePrevious } from './helpers';
import { createGlobalStyle } from 'styled-components';
import { Box } from 'rebass';

export const DragDropContext = React.createContext('');

const debug = false;

const GlobalStyles = createGlobalStyle `
    body {
        ${
      props => props.isDragging ? `
          user-select: none;
          height: 100%;
          overflow: hidden;
      ` : ''
    };
    }
`;
  

const DragDropProvider = ({ children, onDragEnd }) => {
  const [uniqueId, setUniqueId] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const prevIsDragging = usePrevious(isDragging);
  const [initialMousePosition, setInitialMousePosition] = useState({x: 0, y: 0});

  const [draggingItemId, setDraggingItemId] = useState(false);
  const draggingItemPlaceholder = document.querySelector(`[data-draggable-id="${draggingItemId}"]`);

  const [startingDroppableId, setStartingDroppableId] = useState(false);
  const [hoveringDroppableId, setHoveringDroppableId] = useState(false);
  const [startingIndex, setStartingIndex] = useState(false);

  const timer = useRef(null);

  // Window events
  useEffect(() => {
    function handleMouseUp(e) {
      clearTimeout(timer.current);
      
      setIsDragging(false);

      const draggingItemGhost = document.querySelector(`[data-ghost="${draggingItemId}"]`);
      draggingItemGhost.remove();

      const dropIndicatorEl = document.querySelector('.drop-indicator');
      if (dropIndicatorEl) {
        dropIndicatorEl.parentNode.insertBefore(draggingItemPlaceholder, dropIndicatorEl);
      }

      // Get index of dropIndicator in parent
      const endingIndex = Array.from(draggingItemPlaceholder.parentNode.children).indexOf(draggingItemPlaceholder);

      // Get closest droppable to dropIndicator
      const droppable = dropIndicatorEl.closest("[data-droppable-id]");
      const droppableId = droppable.getAttribute("data-droppable-id");

      dropIndicatorEl.style.display = 'none';

      draggingItemPlaceholder.style.opacity = '1';

      setDraggingItemId(null);
      setHoveringDroppableId(null);

      // Reset that juicy state
      setUniqueId(i => i + 2);

      onDragEnd({
        id: draggingItemId,
        from: {
          droppableId: startingDroppableId,
          index: startingIndex
        },
        to: {
          droppableId: droppableId,
          index: endingIndex
        }
      })
    }

    function cancelDrop() {
      const dropIndicatorEl = document.querySelector('.drop-indicator');
      dropIndicatorEl.style.display = 'none';
      setHoveringDroppableId(null);
    }

    function handleMoveItem(e) {
      const dropIndicatorEl = document.querySelector('.drop-indicator');

      let elementFromPoint = document.elementFromPoint(e.clientX, e.clientY);
      let draggingOverDraggable = elementFromPoint ? elementFromPoint.closest("[data-draggable-id]") : null;
      let draggingOverDraggableId = draggingOverDraggable ? draggingOverDraggable.getAttribute("data-draggable-id") : null;
      let draggingOverDroppable = elementFromPoint ? elementFromPoint.closest("[data-droppable-id]") : null;
      let draggingOverDroppableId = draggingOverDroppable ? draggingOverDroppable.getAttribute("data-droppable-id") : null;

      // Logic to prevent calculating collisions on items inside the current draggingItem
      // Check if elementFromPoint is a child of draggingItemPlaceholder
      if (draggingItemPlaceholder.contains(elementFromPoint)) {
        draggingOverDraggable = draggingItemPlaceholder;
        draggingOverDraggableId = draggingItemId;
        draggingOverDroppable = draggingItemPlaceholder.closest("[data-droppable-id]");
        draggingOverDroppableId = startingDroppableId;
      }

      // Restrict to draggables that are in the current droppable
      // if (draggingOverDroppableId !== startingDroppableId) {
      //   cancelDrop();
      //   return;
      // } 


      if (draggingOverDraggable || draggingOverDroppable) {
        dropIndicatorEl.style.display = 'block';
        
        // if just hoverin over a droppable
        if (
          (draggingOverDroppableId && !draggingOverDraggableId) ||
          ((draggingOverDraggableId === draggingOverDroppableId) && draggingOverDraggableId !== null)
        ) {
          setHoveringDroppableId(draggingOverDroppableId);

          // Insert dropIndicator inside droppable
          draggingOverDroppable.appendChild(dropIndicatorEl);

        
        } else if (draggingOverDraggableId) {
          setHoveringDroppableId(draggingOverDroppableId);

          if (elementFromPoint && draggingOverDraggable) {
            const { top, height } = draggingOverDraggable.getBoundingClientRect();
            const midpoint = top + (height / 2);
    
            if (e.clientY < midpoint) {

              // Check if dropIndicatorEl is before draggingOverDraggable
              if (dropIndicatorEl.nextSibling !== draggingOverDraggable || !dropIndicatorEl.nextSibling) {
                draggingOverDraggable.parentNode.insertBefore(dropIndicatorEl, draggingOverDraggable);
              }
            } else if (e.clientY > midpoint) {

              // Check if dropIndicatorEl is after draggingOverDraggable
              if (dropIndicatorEl.nextSibling !== draggingOverDraggable.nextSibling || !dropIndicatorEl.nextSibling) {
                draggingOverDraggable.parentNode.insertBefore(dropIndicatorEl, draggingOverDraggable.nextSibling);
              }
            }
          }
        }
      } else {
        cancelDrop();
      }
    }

    function handleMouseMove(e) {
      handleAutoScroll(e, timer, handleMoveItem);

      const draggingItemGhost = document.querySelector(`[data-ghost="${draggingItemId}"]`);

      // Move draggingItemGhost with mouse
      draggingItemGhost.style.transform = `translate(${e.clientX - initialMousePosition.x}px, ${e.clientY - initialMousePosition.y}px)`;

      handleMoveItem(e);
    }

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
  }, [draggingItemPlaceholder, draggingItemId, isDragging, onDragEnd, startingDroppableId, startingIndex, initialMousePosition]);


  useEffect(() => {
    if (isDragging && !prevIsDragging) {
      // Create placholder bar
      const dropIndicatorEl = document.querySelector('.drop-indicator');

      // Create ghost version of draggingItemEl
      const ghost = draggingItemPlaceholder.cloneNode(true);
      ghost.dataset.ghost = draggingItemId;
      ghost.dataset.draggableId = null;
      document.body.appendChild(ghost);

      // Set ghost position
      const { left, top, width, height } = draggingItemPlaceholder.getBoundingClientRect();

      ghost.style.left = `${left}px`;
      ghost.style.top = `${top}px`;
      ghost.style.width = `${width}px`;
      ghost.style.height = `${height}px`;
      ghost.style.opacity = '0.5';
      ghost.style.position = 'fixed';
      ghost.style.pointerEvents = 'none';

      // Check if initialMousePosition is above or below the midpoint of draggingItemEl
      const midpoint = top + (height / 2);

      if (initialMousePosition.y < midpoint) {
        draggingItemPlaceholder.parentNode.insertBefore(dropIndicatorEl, draggingItemPlaceholder);
      } else if (initialMousePosition.y > midpoint) {
        draggingItemPlaceholder.parentNode.insertBefore(dropIndicatorEl, draggingItemPlaceholder.nextSibling);
      }

      dropIndicatorEl.style.display = 'block';
    }
  }, [draggingItemPlaceholder, draggingItemId, initialMousePosition.y, isDragging, prevIsDragging, startingDroppableId]);

  const context = {
    isDragging, setIsDragging,
    draggingItemId, setDraggingItemId,
    startingDroppableId, setStartingDroppableId,
    initialMousePosition, setInitialMousePosition,
    setStartingIndex,
    hoveringDroppableId,
    setHoveringDroppableId
  }

  return (
    <DragDropContext.Provider value={context}>
      <div key={uniqueId}>
        {debug && <Box
          css={`
            position: fixed;
            left: 0;
            top: 0;
            z-index: 999;
          `}
        >
          {hoveringDroppableId}
        </Box>}
        <GlobalStyles isDragging={isDragging} />
        {children()}
        <Box
          className="drop-indicator"
          data-drop-indicator="true"
          css={`
            pointer-events: none;
            border-bottom: 2px solid var(--bar);
            margin-top: -2px;
            position: relative;
            display: none;
          `}
        >
          <Box
            css={`
              height: 10px;
              width: 10px;
              position: absolute;
              left: -5px;
              top: -4px;
              border-radius: 50%;
              background: var(--bar);
              pointer-events: none;
            `}
          />
        </Box>
      </div>
    </DragDropContext.Provider>
  );
};

export default DragDropProvider;
